/**
 * @fileoverview MCP personal access token service using axios client and Zod validation.
 * @module features/mcp/api/mcpTokenService
 */

import { z } from 'zod'
import apiClient from '@/lib/apiClient'
import {
  IssuedMcpTokenSchema,
  McpTokenSchema,
  type IssuedMcpToken,
  type McpToken,
} from '../schemas/mcpToken'

/** Request schema for issuing a new MCP token. */
export const CreateMcpTokenRequestSchema = z.object({
  name: z.string().max(128).nullable(),
})
export type CreateMcpTokenRequest = z.infer<typeof CreateMcpTokenRequestSchema>

/**
 * Issue a new MCP personal access token for the current user.
 * The plaintext token value is returned only once in the response; callers
 * MUST surface it to the user immediately as it cannot be retrieved later.
 *
 * @param body - Token creation request (optional human-readable name)
 * @returns The newly issued token including its plaintext value
 */
export async function issueMcpToken(body: CreateMcpTokenRequest): Promise<IssuedMcpToken> {
  const safe = CreateMcpTokenRequestSchema.parse(body)
  const res = await apiClient.post('/mcp/tokens', safe)
  return IssuedMcpTokenSchema.parse(res.data)
}

/**
 * List MCP tokens belonging to the current user.
 * Returns metadata only — plaintext token values are never disclosed here.
 *
 * @returns Array of MCP token metadata records
 */
export async function listMcpTokens(): Promise<McpToken[]> {
  const res = await apiClient.get('/mcp/tokens')
  return z.array(McpTokenSchema).parse(res.data)
}

/**
 * Revoke an MCP token by id. The token is immediately invalidated server-side.
 *
 * @param id - UUID of the token to revoke
 */
export async function revokeMcpToken(id: string): Promise<void> {
  const safeId = z.string().uuid().parse(id)
  await apiClient.delete(`/mcp/tokens/${safeId}`)
}
